import { useMemo } from 'react';
import classNames from 'classnames';

import type { PackageType, PackageTier } from '@client/api';

import { OnboardingActions } from '../../onboarding/onboarding-actions';

import type { OnboardingBuilding } from '../../onboarding/onboarding.types';
import type { WizardBuildingPackage } from './building-wizard.types';

const PACKAGE_TYPES: PackageType[] = ['Portfolio', 'Management', 'Eos'];
const TIERS: PackageTier[] = ['Basic', 'Standard', 'Premium'];

const PACKAGE_LABELS: Record<PackageType, string> = {
  Portfolio: 'Portefølje',
  Management: 'Forvaltning',
  Eos: 'Energi',
};

// Tiers we let the user pick. Premium isn't live yet — kept in the type list
// for visibility but rendered as a disabled chip.
const DISABLED_TIERS: PackageTier[] = ['Premium'];

interface StepPakkerProps {
  buildings: OnboardingBuilding[];
  packagesByBuildingId: Record<string, WizardBuildingPackage[]>;
  onPackagesChange: (next: Record<string, WizardBuildingPackage[]>) => void;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Per-building package picker. Defaults every selected building to
 * Portfolio:Basic — the same baseline portfolio onboarding assigns — so users
 * can ship the wizard without touching this step. From there they can:
 *   - bump Portfolio to Standard / Premium
 *   - add Management and/or Eos packages with any tier
 *   - remove a package by toggling its row off
 *
 * The handler maps these selections to <c>BulkBuildingPackagePayload[]</c> per
 * building and the BE assigns each space the chosen package + tier.
 */
export function StepPakker({
  buildings,
  packagesByBuildingId,
  onPackagesChange,
  onBack,
  onNext,
}: StepPakkerProps) {
  const selectedBuildings = useMemo(() => buildings.filter((b) => b.selected), [buildings]);

  const togglePackage = (buildingId: string, type: PackageType) => {
    const current = packagesByBuildingId[buildingId] ?? [];
    const exists = current.some((p) => p.packageType === type);

    let next: WizardBuildingPackage[];
    if (exists) {
      next = current.filter((p) => p.packageType !== type);
    } else {
      next = [...current, { packageType: type, tier: 'Basic' }];
    }

    onPackagesChange({ ...packagesByBuildingId, [buildingId]: next });
  };

  const setTier = (buildingId: string, type: PackageType, tier: PackageTier) => {
    const current = packagesByBuildingId[buildingId] ?? [];
    const next = current.map((p) => (p.packageType === type ? { ...p, tier } : p));
    onPackagesChange({ ...packagesByBuildingId, [buildingId]: next });
  };

  const isPicked = (buildingId: string, type: PackageType) =>
    (packagesByBuildingId[buildingId] ?? []).some((p) => p.packageType === type);

  const tierOf = (buildingId: string, type: PackageType): PackageTier =>
    (packagesByBuildingId[buildingId] ?? []).find((p) => p.packageType === type)?.tier ?? 'Basic';

  const everyBuildingHasAtLeastOne = selectedBuildings.every(
    (b) => (packagesByBuildingId[b.id] ?? []).length > 0,
  );

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Pakker</h2>
      <p className="text-white/60 mb-8">
        Velg hvilke pakker som skal aktiveres per bygg.
      </p>

      {selectedBuildings.length === 0 ? (
        <div className="p-4 rounded-lg border border-border bg-surface-base text-white/50 text-sm">
          Ingen bygg er valgt. Gå tilbake og legg til minst ett bygg først.
        </div>
      ) : (
        <div className="space-y-4">
          {selectedBuildings.map((building) => (
            <div
              key={building.id}
              className="p-4 rounded-lg border border-border bg-surface-base"
            >
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <div className="text-white font-medium">{building.name ?? building.address}</div>
                  <div className="text-white/40 text-xs mt-0.5">
                    {building.address} · {building.bra.toLocaleString('nb-NO')} m²
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {PACKAGE_TYPES.map((type) => {
                  const picked = isPicked(building.id, type);
                  const currentTier = tierOf(building.id, type);
                  return (
                    <div
                      key={type}
                      className={classNames(
                        'flex items-center justify-between gap-3 p-3 rounded border transition-colors',
                        picked
                          ? 'border-brand-primary/40 bg-brand-primary/5'
                          : 'border-border bg-surface-base/50',
                      )}
                    >
                      <label className="flex items-center gap-3 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={picked}
                          onChange={() => togglePackage(building.id, type)}
                          className="h-4 w-4 rounded border-white/30 bg-transparent accent-brand-primary"
                        />
                        <span className={classNames('text-sm', picked ? 'text-white' : 'text-white/60')}>
                          {PACKAGE_LABELS[type]}
                        </span>
                      </label>

                      {picked && (
                        <div className="flex gap-1">
                          {TIERS.map((tier) => {
                            const disabled = DISABLED_TIERS.includes(tier);
                            return (
                              <button
                                key={tier}
                                type="button"
                                disabled={disabled}
                                onClick={() => !disabled && setTier(building.id, type, tier)}
                                title={disabled ? 'Kommer snart' : undefined}
                                className={classNames(
                                  'px-3 py-1 text-xs rounded-full font-semibold transition-colors',
                                  disabled
                                    ? 'bg-white/5 text-white/30 cursor-not-allowed'
                                    : currentTier === tier
                                    ? 'bg-brand-primary text-black'
                                    : 'bg-white/5 text-white/60 hover:bg-white/10',
                                )}
                              >
                                {tier}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <OnboardingActions
        onBack={onBack}
        onNext={onNext}
        nextDisabled={selectedBuildings.length === 0 || !everyBuildingHasAtLeastOne}
      />
    </div>
  );
}
